import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  Description as FileIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

// Configurar el worker de PDF.js
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

function OrderViewer({
  files = [],
  onFileUpload,
  onFileDelete,
  onFileDownload,
  readOnly = false
}) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type === 'application/pdf' || file.type === 'application/zip') {
        setIsUploading(true);
        setError(null);
        onFileUpload(file)
          .then(() => {
            setIsUploading(false);
            event.target.value = null; // Reset input
          })
          .catch((err) => {
            setError(err.message || 'Error al subir el archivo');
            setIsUploading(false);
          });
      } else {
        setError('Solo se permiten archivos PDF o ZIP');
      }
    }
  };

  const handleFileOpen = async (file) => {
    const fileType = file.mimetype || file.type;
    if (fileType === 'application/pdf') {
      try {
        // Obtener el archivo como blob para el visor PDF
        const token = localStorage.getItem('token');
        const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';
        const fileUrl = `${baseUrl}/api/orders/files/${file.id}/download`;
        
        const response = await fetch(fileUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Error al cargar el archivo');
        }
        
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        
        // Crear una copia del archivo con la URL del blob
        const fileWithUrl = {
          ...file,
          url: blobUrl
        };
        
        setSelectedFile(fileWithUrl);
        setPageNumber(1);
      } catch (error) {
        setError('Error al cargar el archivo para previsualización');
        console.error('Error al cargar PDF:', error);
      }
    } else {
      onFileDownload(file);
    }
  };

  const handleDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const handleClosePdf = () => {
    // Limpiar el blob URL para evitar memory leaks
    if (selectedFile?.url && selectedFile.url.startsWith('blob:')) {
      URL.revokeObjectURL(selectedFile.url);
    }
    setSelectedFile(null);
    setPageNumber(1);
    setNumPages(null);
  };

  const getFileIcon = (file) => {
    const fileType = file.mimetype || file.type;
    if (fileType === 'application/pdf') {
      return <PdfIcon color="error" />;
    }
    return <FileIcon />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Archivos Adjuntos</Typography>
        {!readOnly && (
          <Button
            component="label"
            variant="contained"
            startIcon={<AddIcon />}
            disabled={isUploading}
          >
            {isUploading ? 'Subiendo...' : 'Añadir Archivo'}
            <input
              type="file"
              hidden
              accept=".pdf,.zip"
              onChange={handleFileSelect}
              disabled={isUploading}
            />
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <List>
        {files.map((file, index) => (
          <React.Fragment key={file.id || index}>
            <ListItem
              button
              onClick={() => handleFileOpen(file)}
              sx={{ cursor: 'pointer' }}
            >
              <ListItemIcon>
                {getFileIcon(file)}
              </ListItemIcon>
              <ListItemText
                primary={file.filename || file.name}
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  onClick={(e) => {
                    e.stopPropagation();
                    onFileDownload(file);
                  }}
                  sx={{ mr: 1 }}
                >
                  <DownloadIcon />
                </IconButton>
                {!readOnly && (
                  <IconButton
                    edge="end"
                    onClick={(e) => {
                      e.stopPropagation();
                      onFileDelete(file);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </ListItemSecondaryAction>
            </ListItem>
            {index < files.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </List>

      {/* Visor de PDF */}
      <Dialog
        open={Boolean(selectedFile)}
        onClose={handleClosePdf}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {selectedFile?.filename || selectedFile?.name}
            <IconButton onClick={handleClosePdf}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Document
              file={selectedFile?.url}
              onLoadSuccess={handleDocumentLoadSuccess}
              loading={
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              }
              error={
                <Alert severity="error">
                  Error al cargar el PDF. Por favor, inténtelo de nuevo.
                </Alert>
              }
            >
              <Page
                pageNumber={pageNumber}
                width={Math.min(window.innerWidth * 0.8, 800)}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Document>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={pageNumber <= 1}
            onClick={() => setPageNumber(pageNumber - 1)}
          >
            Anterior
          </Button>
          <Typography>
            Página {pageNumber} de {numPages || '--'}
          </Typography>
          <Button
            disabled={pageNumber >= numPages}
            onClick={() => setPageNumber(pageNumber + 1)}
          >
            Siguiente
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default OrderViewer; 